/**
 * Owlex compositor (Cloudflare Worker) — canonical trait types + R2 hints.
 * @see workers/owlex-compositor GET /health
 */

import { getStakingEnv } from "./env.js";

/**
 * @param {string} base compositor base — absolute URL or same-origin path e.g. `/api/compositor`
 */
function resolveCompositorBase(base) {
  const raw = String(base ?? "").trim().replace(/\/+$/, "");
  if (!raw) return "";
  if (raw.startsWith("/")) {
    if (typeof window === "undefined") return raw;
    return `${window.location.origin}${raw}`;
  }
  return raw;
}

/** Strip UTF-8 BOM so `<!doctype` checks work. */
function stripBom(s) {
  return String(s).replace(/^\uFEFF/, "");
}

/**
 * True if the body is clearly not JSON (e.g. Cloudflare/HTML error page, SPA shell).
 * Worker `/health` always returns a JSON object `{ ... }`.
 */
function responseBodyLooksLikeMarkup(text) {
  const t = stripBom(text).trimStart();
  if (!t.length) return false;
  return t.charCodeAt(0) === 60 /* < */;
}

/**
 * @typedef {Object} CompositorTraitSlug
 * @property {string} canonical
 * @property {string} slug
 * @property {string[]} [r2FolderAttempts]
 */

/**
 * @typedef {Object} CompositorHealth
 * @property {boolean} ok
 * @property {string} [service]
 * @property {string[]} [canonicalTraitTypes]
 * @property {CompositorTraitSlug[]} [traitSlugs]
 * @property {string} [r2LayerPrefix]
 * @property {string} [r2Hint]
 * @property {string} [error]
 */

/** Matches config/trait-types.json onChainHooks */
export const HEAD_TRAIT_TYPE_ID = 5;
export const OUTFIT_TRAIT_TYPE_ID = 8;

/** Fallback when `/health` is unavailable — same order as on-chain `traitTypeId` 0..8. */
export const CANONICAL_TRAIT_TYPE_NAMES = Object.freeze([
  "Background",
  "Beak",
  "Earring",
  "Eyes",
  "Eyes Accessories",
  "Head and hair",
  "Legs",
  "Owl",
  "outfit",
]);

/**
 * Same trait names as Worker `GET /health` when the proxy or network fails (HTML error page, etc.).
 * @param {string} healthUrl
 * @returns {CompositorHealth & { url: string }}
 */
function compositorHealthFromLocalCatalog(healthUrl) {
  console.warn(
    "[owlex-staking] compositor GET /health unusable — using baked-in CANONICAL_TRAIT_TYPE_NAMES (trait images still use Worker/R2 when reachable).",
    healthUrl
  );
  return {
    ok: true,
    service: "client-catalog-fallback",
    canonicalTraitTypes: [...CANONICAL_TRAIT_TYPE_NAMES],
    traitSlugs: [],
    url: healthUrl,
  };
}

/**
 * @param {string} [baseUrl] override (default from env)
 * @returns {Promise<CompositorHealth & { url: string }>}
 */
export async function fetchCompositorHealth(baseUrl) {
  const configured = baseUrl || getStakingEnv().compositorUrl || "";
  const base = resolveCompositorBase(configured);
  if (!base) {
    return { ok: false, error: "No compositor URL", url: "" };
  }
  const healthUrl = `${base}/health`;
  try {
    const res = await fetch(healthUrl, { method: "GET", credentials: "omit" });
    const text = await res.text();

    if (responseBodyLooksLikeMarkup(text)) {
      return compositorHealthFromLocalCatalog(healthUrl);
    }

    if (!res.ok) {
      const trim = stripBom(text).trimStart();
      return {
        ok: false,
        error: `HTTP ${res.status}${trim && !trim.startsWith("{") ? `: ${trim.slice(0, 200)}` : ""}`,
        url: healthUrl,
      };
    }

    let data;
    try {
      data = JSON.parse(text);
    } catch {
      return compositorHealthFromLocalCatalog(healthUrl);
    }
    if (!data || typeof data !== "object" || Array.isArray(data)) {
      return compositorHealthFromLocalCatalog(healthUrl);
    }
    return {
      ...data,
      ok: Boolean(data.ok),
      url: healthUrl,
    };
  } catch (e) {
    const err = e instanceof Error ? e.message : String(e);
    console.warn("[owlex-staking] fetchCompositorHealth", err);
    return compositorHealthFromLocalCatalog(healthUrl);
  }
}
